export type WeekStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'revoked';
export type ViewMode = 'daily' | 'weekly' | 'monthly';

/** Task line shown in the monthly calendar day hover popover. */
export interface MonthDayProjectTaskDetail {
  taskName: string;
  loggedHours: number;
  comment: string | null;
}

/** One project row for a calendar day (from timesheet-data `by_date`). */
export interface MonthDayProjectDetail {
  projectId: number;
  projectName: string;
  projectCode: string;
  projectType: string;
  status: WeekStatus;
  statusRaw: string;
  loggedHours: number;
  screenHours: number;
  tasks: MonthDayProjectTaskDetail[];
}
export type EntryType = 'crm' | 'sup';
export type SupMode = 'std' | 'crm';

/** Row shape from GET /project-timesheet/timesheet-data `activity[]`. */
export type TimesheetActivityType = 'manual' | 'tracked' | 'untracked';

/** One line in a timesheet-data `activity.tasks[]` (stored on synthesized entries). */
export interface TimesheetActivityTaskLine {
  /** Present when API sends a CRM task id (`task_id` or `taskId`). */
  taskId?: number;
  loggedHours: number;
  stageName?: string;
  /** Phase description from API (shown in brackets beside phase name). */
  stageDescription?: string;
  milestoneName?: string;
  /** Milestone description from API (shown in brackets beside milestone name). */
  milestoneDescription?: string;
  taskName?: string;
  /** From timesheet-data `tasks[].comment` (map/edit modal prefill). */
  comment?: string;
}



/** Populated from GET /master/all-activity-apps. */
let activityAppNameById: ReadonlyMap<number, string> | null = null;

export function setTimesheetActivityAppNames(apps: { id: number; name: string }[]): void {
  activityAppNameById = new Map(apps.map((a) => [a.id, a.name.trim()]));
}

/** Resolves `app_id` to the label shown on tracked (and untracked) activity badges. */
export function timesheetAppDisplayName(appId: number | null | undefined): string {
  if (appId == null) return 'No data found';
  const id = Number(appId);
  const fromApi = activityAppNameById?.get(id);
  if (fromApi) return fromApi;
  return 'No data found';
  // const label = TIMESHEET_APP_DISPLAY_NAME[id];
  // return label ? label.trim() : `App ${id}`;
}

/** From backend: technical → CRM architects; support → HR / non-technical, etc. */
export type EmployeeTimesheetCategory = 'technical' | 'support';

/** Profile used to decide which “Project type” cards and CRM projects are available. */
export interface TimesheetEmployeeProfile {
  category: EmployeeTimesheetCategory;
  /** CRM project names this user may log against (subset of org projects). Empty for support with no CRM access. */
  assignedCrmProjects: string[];
}

export interface TimesheetEntry {
  id: string;
  date: string;
  proj: string;
  projectCode?: string;
  color: string;
  stage: string;
  ms: string;
  task: string;
  hrs: number;
  screen: number;
  comment: string;
  type: EntryType;
  billable: boolean;
  auto: boolean;
  unrId: string | null;
  /** Set when row is synthesized from timesheet-data `activity` (one entry per activity). */
  activityType?: TimesheetActivityType;
  /** App / product name for tracked & untracked badges. */
  activityAppLabel?: string;
  /** Optional backend `activity_title` shown next to activity badge. */
  activityTitle?: string;
  /** Manual row: `tasks.length` from API for subtitle. */
  activityTaskCount?: number;
  /** When API provides a window (e.g. unreported linkage). */
  activityTimeRange?: string;
  /** From timesheet-data: CRM mapping already saved for this activity. */
  activityMapped?: boolean;
  /** Task rows from API for map/edit modal prefill. */
  activityTaskLines?: TimesheetActivityTaskLine[];
  /** CRM `project_id` from timesheet-data (for task board without project-list). */
  timesheetProjectId?: number;
  /** Submission state for this project on this day from timesheet-data `status`. */
  projectDayStatus?: WeekStatus;
  /** Original API `status` string (e.g. submitted vs pending) for display. */
  projectDayStatusRaw?: string;
  /** Activity row id from timesheet-data (`activity_id` / `activityId`) for tracked/untracked POST map. */
  timesheetActivityId?: number;
  /** Index of this row’s `activity[]` item when API omits `activity_id` (groups manual multi-task lines). */
  activityIndex?: number;
}

export interface UnreportedActivity {
  id: string;
  /** Primary line: API `remark` when set (except placeholder NA/N/A), else `taskName`, else enum app name. */
  title: string;
  /** Backend activity app id (for name lookup). */
  appId?: number | null;
  /** From `appId` via {@link timesheetAppDisplayName}. */
  appName: string;
  hrs: number;
  date: string;
  mapped: boolean;
  /** Optional clock window (mock / future API). */
  timeRange?: string;
}

export interface ApprovalHistoryEvent {
  type: 'submitted' | 'approved' | 'rejected' | 'created';
  actor: string;
  role: string;
  date: string;
  note: string | null;
}

export interface CrmProject {
  color: string;
  stages: string[];
}

export type AssignedTaskStatus = 'ongoing' | 'completed' | 'yet_to_start' | 'delayed';

export interface AssignedTask {
  proj: string;
  color: string;
  path: string;
  hrs: number;
  /** Hours consumed (logged). Optional; computed from entries if not set. */
  consumed?: number;
  /** UI status for the task. */
  status?: AssignedTaskStatus;
}

export interface StatCard {
  label: string;
  value: string;
  sub: string;
  icon: 'clock' | 'monitor' | 'dollar' | 'zap';
  bgColor: string;
  textColor: string;
}

export interface WeekDay {
  date: Date;
  dateStr: string;
  dayName: string;
  isToday: boolean;
  isFuture: boolean;
  totalHrs: number;
}

/** One RA activity (tracked/untracked/manual) with one row per CRM task line. */
export interface DailyActivityGroup {
  activityKey: string;
  activityType: TimesheetActivityType;
  activityAppLabel?: string;
  timesheetActivityId?: number;
  activityIndex?: number;
  rows: TimesheetEntry[];
  totalHrs: number;
  totalScreen: number;
}

/** Daily view: project with task rows (each task may expand to show its entries). */
export interface DailyProjectGroup {
  proj: string;
  projectCode?: string;
  /** First entry’s type; drives CRM / Support pill. */
  entryType?: EntryType;
  color: string;
  totalHrs: number;
  /** API activities grouped under one header; tasks as child rows. */
  activities: DailyActivityGroup[];
  /** Non–activity-id rows (manual CRM tasks, unreported, legacy). */
  tasks: DailyTaskGroup[];
  /** From API `status` on timesheet-data project row (same day). */
  submissionStatus?: WeekStatus;
  /** Original API `status` for pill label (submitted vs pending). */
  submissionStatusRaw?: string;
}

export interface DailyTaskGroup {
  taskKey: string;
  taskName: string;
  stage: string;
  ms: string;
  stageDescription?: string;
  msDescription?: string;
  entries: TimesheetEntry[];
  totalHrs: number;
  totalScreen: number;
}

export interface WeeklyProjectGroup {
  proj: string;
  projectCode?: string;
  entryType?: EntryType;
  color: string;
  tasks: WeeklyTaskGroup[];
  /** Rollup of API `status` across this project’s days shown in this week (before suppressed-row removal). */
  weekSubmissionStatus?: WeekStatus;
  /** Logged hours from API rows hidden in weekly (no task label); included in project total. */
  suppressedLoggedHrs?: number;
  /** Screen hours from hidden rows; included in project screen chip. */
  suppressedScreenHrs?: number;
}

export interface WeeklyTaskGroup {
  taskKey: string;
  taskName: string;
  isAuto: boolean;
  entryIds: string[];
  /** Per date: array of entries (supports multiple entries per task+date). */
  entriesByDate: Record<string, TimesheetEntry[]>;
  totalScreenHrs: number;
  comments: string[];
}

export interface ModalState {
  addEntry: boolean;
  addTask: boolean;
  mapToProject: boolean;
  mapToDifferentProject: boolean;
  deleteConfirm: boolean;
  editComment: boolean;
  submitConfirm: boolean;
  assignedTasks: boolean;
}

export interface FormState {
  type: EntryType | null;
  supMode: SupMode;
  billable: boolean | null;
  supBillable: boolean | null;
  customTask: boolean;
  editId: string | null;
  mapId: string | null;
  targetDate: string | null;
  /** When mapping from a project row: locked project in modal. */
  mapProjectName: string | null;
  mapProjectColor: string | null;
  /** Screen hours from linked entry (system-tracked). */
  mapScreenDetectedHrs: number | null;
  /** When mapping from a timesheet row: CRM vs Support segment default. */
  mapLockedEntryType: EntryType | null;
  /** Side-panel bulk map: two or more unreported entry ids (single entry uses `mapId`). */
  mapBulkIds: string[] | null;
}
